/**
 * i18n engine — Aura Nota frontend
 *
 * Supports 6 locales: pt-BR (source), en, es, zh-CN, fr, it.
 * Falls back to pt-BR for any missing key, then to the key itself.
 *
 * Usage:
 *   import { LocaleProvider, useT, useLocale } from './i18n';
 *
 *   function App() {
 *     return <LocaleProvider>...</LocaleProvider>;
 *   }
 *
 *   function MyComponent() {
 *     const t = useT();               // just the t function
 *     const { locale, setLocale } = useLocale();  // locale + setter
 *     return <h1>{t('home.title')}</h1>;
 *   }
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ptBR } from './locales/pt-BR';
import { en } from './locales/en';
import { es } from './locales/es';
import { zhCN } from './locales/zh-CN';
import { fr } from './locales/fr';
import { it } from './locales/it';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Locale = 'pt-BR' | 'en' | 'es' | 'zh-CN' | 'fr' | 'it';

export type TranslationMap = Record<string, string>;

export type TFunction = (key: string, params?: Record<string, string | number>) => string;

// ---------------------------------------------------------------------------
// Translations registry
// ---------------------------------------------------------------------------
// pt-BR is the source locale, always loaded.
// Other locales are registered here once their files are created.
// Until a locale file exists, it maps to an empty object so the fallback
// chain naturally resolves to pt-BR.

const translations: Record<Locale, TranslationMap> = {
  'pt-BR': ptBR,
  en,
  es,
  'zh-CN': zhCN,
  fr,
  it,
};

// Allow lazy registration of additional locale bundles (e.g. when en.ts is
// created in a later task).  This keeps the engine forward-compatible.
export function registerLocale(locale: Locale, map: TranslationMap): void {
  translations[locale] = { ...translations[locale], ...map };
}

// ---------------------------------------------------------------------------
// Core translation function (pure, no React)
// ---------------------------------------------------------------------------

/**
 * Resolve a translation key for a given locale.
 *
 * Lookup order:
 *   1. current locale
 *   2. pt-BR (fallback)
 *   3. the key itself
 *
 * Supports `{param}` interpolation, e.g.:
 *   t('audit.eventCount', { count: 42 })  →  "42 eventos"
 */
function translate(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>,
): string {
  let str = translations[locale]?.[key];
  if (str === undefined) {
    str = translations['pt-BR']?.[key];
  }
  if (str === undefined) {
    return key;
  }
  if (params) {
    for (const [param, value] of Object.entries(params)) {
      str = str.replace(new RegExp(`\\{${param}\\}`, 'g'), String(value));
    }
  }
  return str;
}

// ---------------------------------------------------------------------------
// React Context
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'auranota_locale';

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TFunction;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

// ---------------------------------------------------------------------------
// Locale detection helpers
// ---------------------------------------------------------------------------

function detectInitialLocale(): Locale {
  // 1. localStorage
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && isLocale(stored)) {
    return stored;
  }
  // 2. navigator.language
  const nav = navigator.language;
  if (nav) {
    const lower = nav.toLowerCase();
    // Exact match (e.g. "pt-BR")
    if (isLocale(lower)) return lower as Locale;
    // Prefix match (e.g. "pt" → pt-BR, "en-US" → en)
    const prefix = lower.split('-')[0];
    const match = LOCALE_PREFIXES[prefix];
    if (match) return match;
  }
  // 3. default
  return 'pt-BR';
}

const LOCALE_PREFIXES: Record<string, Locale> = {
  pt: 'pt-BR',
  en: 'en',
  es: 'es',
  zh: 'zh-CN',
  fr: 'fr',
  it: 'it',
};

const VALID_LOCALES: Locale[] = ['pt-BR', 'en', 'es', 'zh-CN', 'fr', 'it'];

function isLocale(value: string): value is Locale {
  return VALID_LOCALES.includes(value as Locale);
}

// ---------------------------------------------------------------------------
// LocaleProvider
// ---------------------------------------------------------------------------

export function LocaleProvider({
  children,
  preferredLocale,
}: {
  children: ReactNode;
  preferredLocale?: Locale | null;
}) {
  const [locale, setLocaleState] = useState<Locale>(() => detectInitialLocale());

  // Bug #2: when the user is logged in and has a preferred_language stored
  // in the backend, apply it (overrides localStorage / browser detection).
  // Only applies when preferredLocale is a non-null valid locale.
  useEffect(() => {
    if (preferredLocale && isLocale(preferredLocale)) {
      setLocaleState(preferredLocale);
      try {
        localStorage.setItem(STORAGE_KEY, preferredLocale);
      } catch {
        // localStorage may be unavailable — ignore.
      }
    }
  }, [preferredLocale]);

  // Persist locale changes to localStorage.
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage may be unavailable (private mode, SSR) — ignore.
    }
  }, []);

  // Sync <html lang> attribute for accessibility / SEO.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useCallback<TFunction>(
    (key, params) => translate(locale, key, params),
    [locale],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return createElement(LocaleContext.Provider, { value }, children);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Returns the full locale context: { locale, setLocale, t }.
 * Throws if used outside of <LocaleProvider>.
 */
export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return ctx;
}

/**
 * Convenience hook that returns only the `t` function.
 */
export function useT(): TFunction {
  return useLocale().t;
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { translations };
export { VALID_LOCALES, isLocale };
export { STORAGE_KEY };
